import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from 'common/toast';
import DropdownOption from 'components/Common/DropdownOption';
import LabelWrapper from 'components/Common/LabelWrapper';
import { useRole } from 'components/Hooks/UserHooks';
import { AI_MODEL_BOOSTING_NEWS_TRAVEL_OPTIONS, SYSTEM_CONFIG_KEY } from 'helpers/constans';
import { isHavePermissionRole, ROLES_FOR_APP } from 'helpers/role';
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CardBody, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";
import { getConfiguration, putConfiguration } from "../../../../store/thunks";

interface ModalConfigGenerateProps {
    isOpen: boolean;
    onClose: () => void;
}

const ModalConfigGenerate = ({ isOpen = false, onClose = () => { } }: ModalConfigGenerateProps) => {
    const { t, i18n } = useTranslation();
    const { userPermissions } = useRole();

    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [condition, setCondition] = useState<{
        generate_prompt: string,
        ai_model: string
        top_percentage_random_selection: string,
        random_selection_count: string,
        attraction_count: string,
        food_count: string,
        cafe_count: string,
        blocked_places: string,
    } | null>(null);

    const AI_MODEL_NEWS_TRAVEL_OPTIONS_LANG: any = AI_MODEL_BOOSTING_NEWS_TRAVEL_OPTIONS?.map((item: any) => ({ value: item?.value, label: t(item?.label) }));

    const handleSave = async () => {
        try {
            if (isSaving) {
                return;
            }
            setIsSaving((_prev) => true);
            const _config = {
                configs: [
                    {
                        key: SYSTEM_CONFIG_KEY.TRAVEL_NEWS_GENERATE_PROMPT,
                        value: String(condition?.generate_prompt ?? ''),
                        is_active: 1,
                    },
                    {
                        key: SYSTEM_CONFIG_KEY.TRAVEL_NEWS_GENERATE_AI_MODEL,
                        value: String(condition?.ai_model ?? ''),
                        is_active: 1,
                    },
                    {
                        key: SYSTEM_CONFIG_KEY.TRAVEL_NEWS_TOP_PERCENTAGE_RANDOM_SELECTION,
                        value: String(condition?.top_percentage_random_selection ?? ''),
                        is_active: 1,
                    },
                    {
                        key: SYSTEM_CONFIG_KEY.TRAVEL_NEWS_RANDOM_SELECTION_COUNT,
                        value: String(condition?.random_selection_count ?? ''),
                        is_active: 1,
                    },
                    {
                        key: SYSTEM_CONFIG_KEY.TRAVEL_NEWS_SELECTION_RATIO,
                        value: JSON.stringify({
                            attraction: String(condition?.attraction_count)?.trim() === '' ? null : Number(condition?.attraction_count || 0),
                            food: String(condition?.food_count)?.trim() === '' ? null : Number(condition?.food_count || 0),
                            cafe: String(condition?.cafe_count)?.trim() === '' ? null : Number(condition?.cafe_count || 0)
                        }),
                        is_active: 1,
                    },
                    {
                        key: SYSTEM_CONFIG_KEY.TRAVEL_NEWS_BLOCKED_PLACES,
                        value: String(condition?.blocked_places ?? ''),
                        is_active: 1,
                    },
                ]
            };
            const response: any = await putConfiguration(_config);
            setIsSaving((_prev) => false);
            if (response?.code === 200) {
                toast(t("The process has been completed."), CONFIG_OPTION_TOAST_NORMAL);
                onClose?.();
            } else {
                toast(response?.message || response, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsSaving((_prev) => false);
            toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    }


    const handleGetConfig = async () => {
        try {
            const response: any = await getConfiguration();
            if (response?.code === 200) {
                const _configs = response?.data?.list || [];
                const _config_ratio = JSON.parse(_configs?.find((item: any) => item?.key === SYSTEM_CONFIG_KEY.TRAVEL_NEWS_SELECTION_RATIO)?.value || '{}');
                const data = {
                    generate_prompt: _configs?.find((item: any) => item?.key === SYSTEM_CONFIG_KEY.TRAVEL_NEWS_GENERATE_PROMPT)?.value || '',
                    ai_model: _configs?.find((item: any) => item?.key === SYSTEM_CONFIG_KEY.TRAVEL_NEWS_GENERATE_AI_MODEL)?.value || '',
                    top_percentage_random_selection: _configs?.find((item: any) => item?.key === SYSTEM_CONFIG_KEY.TRAVEL_NEWS_TOP_PERCENTAGE_RANDOM_SELECTION)?.value || '-',
                    random_selection_count: _configs?.find((item: any) => item?.key === SYSTEM_CONFIG_KEY.TRAVEL_NEWS_RANDOM_SELECTION_COUNT)?.value || '-',
                    attraction_count: _config_ratio?.attraction !== null ? _config_ratio?.attraction : '',
                    food_count: _config_ratio?.food !== null ? _config_ratio?.food : '',
                    cafe_count: _config_ratio?.cafe !== null ? _config_ratio?.cafe : '',
                    blocked_places: _configs?.find((item: any) => item?.key === SYSTEM_CONFIG_KEY.TRAVEL_NEWS_BLOCKED_PLACES)?.value || '',
                }
                setCondition((_prev: any) => ({ ..._prev, ...data }));
            }
        } catch (error: any) {
            toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    }

    useEffect(() => {
        if (isOpen) {
            handleGetConfig();
        }
    }, [isOpen]);

    return (
        <React.Fragment>
            <Modal
                isOpen={isOpen}
                centered={true}
                size="lg"
                scrollable={false}
            >
                <ModalHeader toggle={() => onClose?.()}>
                    <>{t("Config Generate")}</>
                </ModalHeader>
                <ModalBody className="pt-2">
                    <Row>
                        {isHavePermissionRole(ROLES_FOR_APP.CONFIGURATION_BOOSTING_NEWS_TRAVEL, userPermissions) && (
                            <Col lg={12}>
                                <CardBody className="pt-3">
                                    <Row>
                                        <Col sm={12} md={6} lg={4} xl={4} xxl={4} className='mt-0 mb-3'>
                                            <LabelWrapper label={t('AI Model')} isShow={true} colorText="var(--vz-primary)">
                                                <DropdownOption
                                                    name="ai_model"
                                                    dataList={AI_MODEL_NEWS_TRAVEL_OPTIONS_LANG || []}
                                                    placeholder={`${t("AI Model")}...`}
                                                    className={`dropdown-status-rounded z-hight mb-3 mb-lg-2 mb-xxl-1`}
                                                    classNamePrefix="name-prefix"
                                                    initialValue={AI_MODEL_NEWS_TRAVEL_OPTIONS_LANG?.find((item: any) => item?.value === condition?.ai_model) || AI_MODEL_NEWS_TRAVEL_OPTIONS_LANG[0]}
                                                    onChangeSelect={(e: any) => {
                                                        setCondition((_prev: any) => ({ ..._prev, ai_model: e?.value }))
                                                    }}
                                                />
                                            </LabelWrapper>
                                        </Col>
                                        <Col sm={12} className='mt-1 mb-2'>
                                            <LabelWrapper label={t('Generate Prompt')} isShow={true} colorText="var(--vz-primary)">
                                                <Input
                                                    name="text"
                                                    type="textarea"
                                                    className="form-control mb-3 mb-lg-2 mb-xxl-1"
                                                    placeholder=""
                                                    style={{ height: '200px' }}
                                                    value={condition?.generate_prompt}
                                                    onChange={(event: any) => {
                                                        const input = event.target.value;
                                                        setCondition((_prev: any) => ({ ..._prev, generate_prompt: input }))
                                                    }}
                                                    autoComplete="off"
                                                />
                                                <div className="w-100 mb-1 mb-xl-0 fs-12 text-muted pt-0">
                                                    {t(`{{keywords}} will replace dynamically`, { keywords: `{chat_history_block}, {search_query}, {current_date}, {weather_info}, {places_data_json}` })}
                                                </div>
                                            </LabelWrapper>
                                        </Col>
                                    </Row>
                                    <div className='border border-dashed position-relative rounded-3 mt-3' style={{ borderColor: "var(--vz-primary)!important" }}>
                                        <span className='text-primary position-absolute fs-13 px-1' style={{ backgroundColor: "var(--vz-secondary-bg)", left: '8px', top: '0px', transform: 'translateY(-50%)' }}>{t('Percentage for Random Selection')}</span>
                                        <div className='p-3 pt-4 d-flex align-items-center justify-content-between'>
                                            <div className='w-100 position-relative'>
                                                <LabelWrapper label={t('From Percentage')} isShow={true}>
                                                    <Input
                                                        name="from_percentage"
                                                        type="number"
                                                        className="form-control mb-3 mb-lg-2 mb-xxl-1"
                                                        placeholder=""
                                                        value={condition?.top_percentage_random_selection?.split('-')?.[0] || ''}
                                                        onChange={(event: any) => {
                                                            const input = event.target.value;
                                                            const onlyNumbers = String(input).replace(/\D/g, '');
                                                            setCondition((_prev: any) => {
                                                                const toPercentage = String(_prev?.top_percentage_random_selection)?.split('-')?.[1] || '';
                                                                return {
                                                                    ..._prev,
                                                                    top_percentage_random_selection: `${onlyNumbers}-${toPercentage}`
                                                                }
                                                            })
                                                        }}
                                                    />
                                                </LabelWrapper>
                                                <span className='text-muted fs-12 position-absolute' style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>%</span>
                                            </div>
                                            <div className='text-center px-2'>~</div>
                                            <div className='w-100 position-relative'>
                                                <LabelWrapper label={t('To Percentage')} isShow={true}>
                                                    <Input
                                                        name="to_percentage"
                                                        type="number"
                                                        className="form-control mb-3 mb-lg-2 mb-xxl-1"
                                                        placeholder=""
                                                        value={condition?.top_percentage_random_selection?.split('-')?.[1] || ''}
                                                        onChange={(event: any) => {
                                                            const input = event.target.value;
                                                            const onlyNumbers = String(input).replace(/\D/g, '');
                                                            setCondition((_prev: any) => {
                                                                const fromPercentage = String(_prev?.top_percentage_random_selection)?.split('-')?.[0] || '';
                                                                return {
                                                                    ..._prev,
                                                                    top_percentage_random_selection: `${fromPercentage}-${onlyNumbers}`
                                                                }
                                                            })
                                                        }}
                                                    />
                                                </LabelWrapper>
                                                <span className='text-muted fs-12 position-absolute' style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>%</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='border border-dashed position-relative rounded-3 mt-3' style={{ borderColor: "var(--vz-primary)!important" }}>
                                        <span className='text-primary position-absolute fs-13 px-1' style={{ backgroundColor: "var(--vz-secondary-bg)", left: '8px', top: '0px', transform: 'translateY(-50%)' }}>{t('Random Selection Count')}</span>
                                        <div className='p-3 pt-4 d-flex align-items-center justify-content-between'>
                                            <div className='w-100 position-relative'>
                                                <LabelWrapper label={t('From Count')} isShow={true}>
                                                    <Input
                                                        name="from_count"
                                                        type="number"
                                                        className="form-control mb-3 mb-lg-2 mb-xxl-1"
                                                        placeholder=""
                                                        value={condition?.random_selection_count?.split('-')?.[0] || ''}
                                                        onChange={(event: any) => {
                                                            const input = event.target.value;
                                                            const onlyNumbers = String(input).replace(/\D/g, '');
                                                            setCondition((_prev: any) => {
                                                                const toPercentage = String(_prev?.random_selection_count)?.split('-')?.[1] || '';
                                                                return {
                                                                    ..._prev,
                                                                    random_selection_count: `${onlyNumbers}-${toPercentage}`
                                                                }
                                                            })
                                                        }}
                                                    />
                                                </LabelWrapper>
                                                <span className='text-muted fs-12 position-absolute' style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>{t('Places')}</span>
                                            </div>
                                            <div className='text-center px-2'>~</div>
                                            <div className='w-100 position-relative'>
                                                <LabelWrapper label={t('To Count')} isShow={true}>
                                                    <Input
                                                        name="to_count"
                                                        type="number"
                                                        className="form-control mb-3 mb-lg-2 mb-xxl-1"
                                                        placeholder=""
                                                        value={condition?.random_selection_count?.split('-')?.[1] || ''}
                                                        onChange={(event: any) => {
                                                            const input = event.target.value;
                                                            const onlyNumbers = String(input).replace(/\D/g, '');
                                                            setCondition((_prev: any) => {
                                                                const fromPercentage = String(_prev?.random_selection_count)?.split('-')?.[0] || '';
                                                                return {
                                                                    ..._prev,
                                                                    random_selection_count: `${fromPercentage}-${onlyNumbers}`
                                                                }
                                                            })
                                                        }}
                                                    />
                                                </LabelWrapper>
                                                <span className='text-muted fs-12 position-absolute' style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>{t('Places')}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='border border-dashed position-relative rounded-3 mt-3' style={{ borderColor: "var(--vz-primary)!important" }}>
                                        <span className='text-primary position-absolute fs-13 px-1' style={{ backgroundColor: "var(--vz-secondary-bg)", left: '8px', top: '0px', transform: 'translateY(-50%)' }}>{t('Random Type Count')}</span>
                                        <div className='p-3 pt-4 d-flex align-items-center justify-content-between'>
                                            <div className='w-100 position-relative'>
                                                <LabelWrapper label={t('Attraction')} isShow={true}>
                                                    <Input
                                                        name="attraction"
                                                        type="number"
                                                        className="form-control mb-3 mb-lg-2 mb-xxl-1"
                                                        placeholder=""
                                                        value={condition?.attraction_count}
                                                        onChange={(event: any) => {
                                                            const input = event.target.value;
                                                            const onlyNumbers = String(input).replace(/\D/g, '');
                                                            setCondition((_prev: any) => {
                                                                return {
                                                                    ..._prev,
                                                                    attraction_count: onlyNumbers
                                                                }
                                                            })
                                                        }}
                                                    />
                                                </LabelWrapper>
                                                <span className='text-muted fs-12 position-absolute' style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>{t('Places')}</span>
                                            </div>
                                            <div className='text-center px-2'>|</div>
                                            <div className='w-100 position-relative'>
                                                <LabelWrapper label={t('Food')} isShow={true}>
                                                    <Input
                                                        name="food"
                                                        type="number"
                                                        className="form-control mb-3 mb-lg-2 mb-xxl-1"
                                                        placeholder=""
                                                        value={condition?.food_count}
                                                        onChange={(event: any) => {
                                                            const input = event.target.value;
                                                            const onlyNumbers = String(input).replace(/\D/g, '');
                                                            setCondition((_prev: any) => {
                                                                return {
                                                                    ..._prev,
                                                                    food_count: onlyNumbers
                                                                }
                                                            })
                                                        }}
                                                    />
                                                </LabelWrapper>
                                                <span className='text-muted fs-12 position-absolute' style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>{t('Place')}</span>
                                            </div>
                                            <div className='text-center px-2'>|</div>
                                            <div className='w-100 position-relative'>
                                                <LabelWrapper label={t('Cafe')} isShow={true}>
                                                    <Input
                                                        name="cafe_count"
                                                        type="number"
                                                        className="form-control mb-3 mb-lg-2 mb-xxl-1"
                                                        placeholder=""
                                                        value={condition?.cafe_count}
                                                        onChange={(event: any) => {
                                                            const input = event.target.value;
                                                            const onlyNumbers = String(input).replace(/\D/g, '');
                                                            setCondition((_prev: any) => {
                                                                return {
                                                                    ..._prev,
                                                                    cafe_count: onlyNumbers
                                                                }
                                                            })
                                                        }}
                                                    />
                                                </LabelWrapper>
                                                <span className='text-muted fs-12 position-absolute' style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>{t('Places')}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <Col sm={12} md={12} xl={12} className='mt-4'>
                                        <LabelWrapper label={t('Blocked Places')} isShow={true} colorText="var(--vz-primary)">
                                            <Input
                                                className="form-control"
                                                id="blocked_places"
                                                name="blocked_places"
                                                type="textarea"
                                                autoComplete='off'
                                                rows={10}
                                                value={condition?.blocked_places || ''}
                                                placeholder={t('Blocked Places')}
                                                onChange={(e: any) => {
                                                    setCondition((_prev: any) => ({ ..._prev, blocked_places: e?.target?.value || '' }));
                                                }}
                                            />
                                        </LabelWrapper>
                                    </Col>
                                </CardBody>
                            </Col>
                        )}
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <div className="d-flex justify-content-center align-item-center"></div>
                    <button
                        className="btn btn-primary"
                        type="button"
                        onClick={handleSave}
                        style={{ width: '120px' }}
                        disabled={isSaving}
                    >
                        {isSaving ? <Spinner size="sm" className="me-2" ></Spinner> : <></>}
                        {t("Button Save")}
                    </button>
                </ModalFooter>
            </Modal>
        </React.Fragment >
    );
};

export default ModalConfigGenerate;